// FILE: src/pages/TenantMemberManage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import {
  PAGE_ACCESS,
  DEFAULT_MANAGER_MENU_KEYS,
  EXTRA_MANAGER_PAGES,
  buildNoneAccessMap,
  applyManagerDefaults,
  normalizePageAccess,
} from "../access/pageAccess";

const B = {
  red: "#980000", redDark: "#650202", orange: "#f15822",
  navy: "#003353", steel: "#657899", bg: "#f6f7fb",
  white: "#ffffff", border: "#e8ebf3",
};

function Card({ title, children }) {
  return (
    <div style={{
      background: B.white, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: "20px 24px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)", marginBottom: 20,
    }}>
      <h3 style={{ margin: "0 0 16px", color: B.redDark, fontSize: 16, fontWeight: 800 }}>{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderBottom: `1px solid ${B.border}` }}>
      <span style={{ fontSize: 13, color: B.steel, fontWeight: 600 }}>{label}</span>
      <span style={{ fontSize: 13, color: B.redDark, fontWeight: 700, textAlign: "right", maxWidth: "60%", wordBreak: "break-word" }}>{value ?? "—"}</span>
    </div>
  );
}

function Btn({ label, onClick, disabled, color = B.redDark, bg = B.white, solid }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      height: 36, padding: "0 14px", borderRadius: 8,
      border: `1px solid ${color}`,
      background: solid ? color : bg,
      color: solid ? B.white : color,
      fontWeight: 700, fontSize: 13,
      cursor: disabled ? "not-allowed" : "pointer",
      opacity: disabled ? 0.6 : 1,
    }}>{label}</button>
  );
}

const EMPTY_FORM = { name: "", email: "", delivery: "upon_death", deliverAt: null, message: "", includeSecret: false, secretNote: "" };

export default function TenantMemberManage() {
  const { tenantId, memberId } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [overview, setOverview] = useState(null);
  const [accountManagers, setAccountManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [roleDraft, setRoleDraft] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // Modal state
  const [mgrOpen, setMgrOpen] = useState(false);
  const [mgrEditingId, setMgrEditingId] = useState(null);
  const [mgrForm, setMgrForm] = useState(EMPTY_FORM);
  const [mgrPageAccess, setMgrPageAccess] = useState({});
  const [mgrPermBusy, setMgrPermBusy] = useState(false);

  const callApi = useCallback(async (endpoint, body, method = "POST") => {
    const cu = auth.currentUser;
    const token = cu ? await cu.getIdToken() : null;
    const opts = {
      method,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    };
    if (method !== "GET" && body) opts.body = JSON.stringify(body);
    const res = await fetch(endpoint, opts);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAccountManagers() {
    try {
      const data = await callApi(
        `/api/accountManagers/tenantListForMember?tenantId=${encodeURIComponent(tenantId)}&uid=${encodeURIComponent(memberId)}`,
        null, "GET"
      );
      setAccountManagers(Array.isArray(data) ? data : (data.items || data.managers || []));
    } catch (e) {
      console.warn("accountManagers load failed", e.message);
    }
  }

  useEffect(() => {
    async function load() {
      try {
        const memberSnap = await getDoc(doc(db, "tenants", tenantId, "members", memberId));
        if (memberSnap.exists()) {
          const m = { id: memberSnap.id, ...memberSnap.data() };
          setMember(m);
          setRoleDraft(m.role || "employee");
        }
        try {
          const data = await callApi(`/api/getUserOverview?uid=${encodeURIComponent(memberId)}`, null, "GET");
          setOverview(data);
        } catch (e) { console.warn("getUserOverview failed", e.message); }
        await loadAccountManagers();
      } catch (e) {
        setErr("Failed to load member data: " + e.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, memberId, callApi]);

  function openMgr(item) {
    setMgrEditingId(item?.id || null);
    setMgrForm({
      name: item?.name || item?.recipient || "",
      email: item?.email || "",
      delivery: item?.delivery || "upon_death",
      deliverAt: item?.deliverAt ? (item.deliverAt.seconds ? new Date(item.deliverAt.seconds * 1000) : new Date(item.deliverAt)) : null,
      message: item?.message || "",
      includeSecret: !!item?.includeSecret,
      secretNote: item?.secretNote || "",
    });
    const existing = item && typeof item.pageAccess === "object" ? normalizePageAccess(item.pageAccess) : buildNoneAccessMap();
    setMgrPageAccess(applyManagerDefaults(existing));
    setMgrOpen(true);
  }

  function closeMgr() {
    setMgrOpen(false);
    setMgrEditingId(null);
    setMgrForm(EMPTY_FORM);
    setMgrPageAccess(applyManagerDefaults(buildNoneAccessMap()));
    setMgrPermBusy(false);
  }

  function setMgrField(k, v) { setMgrForm(f => ({ ...f, [k]: v })); }
  function adminTogglePage(key, on) { setMgrPageAccess(prev => applyManagerDefaults({ ...(prev || {}), [key]: !!on })); }

  async function saveMgr() {
    setBusy(true);
    try {
      await callApi("/api/accountManagers/tenantUpsert", {
        tenantId, uid: memberId,
        id: mgrEditingId || undefined,
        ...mgrForm,
        deliverAt: mgrForm.delivery === "scheduled" && mgrForm.deliverAt ? new Date(mgrForm.deliverAt).toISOString() : null,
      });
      await loadAccountManagers();
      closeMgr();
      setMsg("Account manager saved.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function deleteMgr() {
    if (!window.confirm("Delete this account manager?")) return;
    setBusy(true);
    try {
      await callApi("/api/accountManagers/tenantDelete", { tenantId, uid: memberId, id: mgrEditingId });
      await loadAccountManagers();
      closeMgr();
      setMsg("Account manager deleted.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function savePermissions() {
    if (!mgrEditingId) return;
    setMgrPermBusy(true);
    try {
      await callApi("/api/accountManagers/tenantSetPermissions", {
        tenantId, uid: memberId, id: mgrEditingId,
        pageAccess: applyManagerDefaults(normalizePageAccess(mgrPageAccess || {})),
      });
      await loadAccountManagers();
      setMsg("Permissions saved.");
    } catch (e) { setErr(e.message); }
    finally { setMgrPermBusy(false); }
  }

  async function handleResendManager(amId) {
    setBusy(true); setMsg(""); setErr("");
    try {
      await callApi("/api/accountManagers/tenantResend", { tenantId, uid: memberId, id: amId });
      setMsg("Invite email resent successfully.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleSaveRole() {
    setBusy(true); setMsg(""); setErr("");
    try {
      await callApi("/api/setTenantMemberRole", { tenantId, memberUid: memberId, role: roleDraft });
      setMember(prev => ({ ...prev, role: roleDraft }));
      setMsg("Role updated successfully.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleSuspend() {
    if (!window.confirm(`${member?.status === "suspended" ? "Reactivate" : "Suspend"} this member?`)) return;
    setBusy(true); setMsg(""); setErr("");
    try {
      const action = member?.status === "suspended" ? "reactivate" : "suspend";
      await callApi("/api/setTenantMemberStatus", { tenantId, memberId, action });
      setMember(prev => ({ ...prev, status: action === "suspend" ? "suspended" : "active" }));
      setMsg(`Member ${action === "suspend" ? "suspended" : "reactivated"} successfully.`);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleSendReset() {
    setBusy(true); setMsg(""); setErr("");
    try {
      await callApi("/api/authAdminSendReset", { email: member?.email || overview?.user?.email });
      setMsg("Password reset email sent.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  async function handleSendVerification() {
    setBusy(true); setMsg(""); setErr("");
    try {
      await callApi("/api/adminUserLookup", { uid: memberId, action: "sendVerification" });
      setMsg("Verification email sent.");
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (loading) return (
    <div style={{ background: B.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "system-ui" }}>
      <div style={{ color: B.steel }}>Loading...</div>
    </div>
  );

  const email = member?.email || overview?.user?.email || "—";
  const status = member?.status || "—";
  const role = member?.role || "—";
  const verified = overview?.user?.emailVerified ? "Yes" : "No";
  const mfa = overview?.profile?.mfa?.enabled ? "Enabled" : "Not enabled";
  const plan = overview?.profile?.plan || overview?.profile?.backendPlan || "—";
  const subStatus = overview?.profile?.subscriptionStatus || "—";
  const storageLimit = overview?.profile?.planLimits?.maxStorageBytes;
  const managerLimit = overview?.profile?.planLimits?.accountManagers;
  const lastLogin = overview?.user?.metadata?.lastSignInTime ? new Date(overview.user.metadata.lastSignInTime).toLocaleString() : "—";

  const inputStyle = { width: "100%", height: 38, padding: "0 12px", border: `1px solid ${B.border}`, borderRadius: 8, fontSize: 13, marginBottom: 10, boxSizing: "border-box" };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: B.steel, display: "block", marginBottom: 4 };

  return (
    <div style={{ background: B.bg, minHeight: "100vh", padding: "24px 20px 120px", fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
          <button onClick={() => navigate(`/tenant/${tenantId}`)} style={{ height: 36, padding: "0 14px", borderRadius: 8, border: `1px solid ${B.border}`, background: B.white, color: B.redDark, fontWeight: 700, fontSize: 13, cursor: "pointer" }}>← Back</button>
          <h1 style={{ margin: 0, color: B.redDark, fontSize: 24, fontWeight: 800 }}>Member Management</h1>
        </div>

        {msg && <div style={{ background: "#ecfdf5", border: "1px solid #6ee7b7", borderRadius: 8, padding: "10px 14px", color: "#065f46", fontSize: 13, marginBottom: 16 }}>{msg}</div>}
        {err && <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "10px 14px", color: "#7f1d1d", fontSize: 13, marginBottom: 16 }}>{err}</div>}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 20 }}>
          <Card title="Account">
            <Row label="Email" value={email} />
            <Row label="Status" value={status} />
            <Row label="Verified" value={verified} />
            <Row label="MFA" value={mfa} />
            <Row label="Last login" value={lastLogin} />
            <div style={{ padding: "10px 0", borderBottom: `1px solid ${B.border}` }}>
              <div style={{ fontSize: 13, color: B.steel, fontWeight: 600, marginBottom: 6 }}>Role</div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <select value={roleDraft} onChange={e => setRoleDraft(e.target.value)} style={{ height: 34, minWidth: 130, borderRadius: 6, border: `1px solid ${B.border}`, padding: "0 8px", fontSize: 13 }}>
                  <option value="owner">owner</option>
                  <option value="hr_admin">hr_admin</option>
                  <option value="employee">employee</option>
                </select>
                <Btn label="Save role" onClick={handleSaveRole} disabled={busy || roleDraft === role} color={B.navy} solid />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
              <Btn label={member?.status === "suspended" ? "Reactivate" : "Suspend"} onClick={handleSuspend} disabled={busy} color={member?.status === "suspended" ? B.navy : B.redDark} solid={member?.status !== "suspended"} />
              <Btn label="Send reset" onClick={handleSendReset} disabled={busy} color={B.navy} />
              <Btn label="Send verification" onClick={handleSendVerification} disabled={busy || verified === "Yes"} color={B.steel} />
            </div>
          </Card>

          <Card title="Plan & Subscription">
            <Row label="Plan" value={plan} />
            <Row label="Subscription status" value={subStatus} />
            <Row label="Storage quota" value={storageLimit ? `${(storageLimit / (1024*1024*1024)).toFixed(0)} GB` : "—"} />
            <Row label="Account manager limit" value={managerLimit || "—"} />
          </Card>

          <Card title={`Account Managers (${accountManagers.length})`}>
            {accountManagers.length === 0 ? (
              <div style={{ fontSize: 13, color: B.steel, marginBottom: 12 }}>No account managers added yet.</div>
            ) : accountManagers.map((am, i) => {
              const accepted = !!(am.acceptedAt);
              return (
                <div key={am.id || i} style={{ padding: "10px 0", borderBottom: `1px solid ${B.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: B.redDark }}>{am.name || am.recipient || "—"}</div>
                      <div style={{ fontSize: 12, color: B.steel, marginTop: 2 }}>{am.email || "—"}</div>
                    </div>
                    <span style={{ fontSize: 11, borderRadius: 999, padding: "2px 10px", fontWeight: 700, background: accepted ? "#ecfdf5" : "#fff0ee", color: accepted ? "#065f46" : B.red, border: `1px solid ${accepted ? "#6ee7b7" : B.orange}` }}>{accepted ? "Accepted" : "Pending"}</span>
                  </div>
                  <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 11, borderRadius: 999, padding: "2px 8px", fontWeight: 700, background: am.readOnly === false ? "#eff6ff" : B.bg, color: am.readOnly === false ? B.navy : B.steel, border: `1px solid ${am.readOnly === false ? "#bfdbfe" : B.border}` }}>{am.readOnly === false ? "Full access" : "Read only"}</span>
                    <button onClick={() => openMgr(am)} style={{ fontSize: 12, fontWeight: 700, color: B.navy, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>Edit</button>
                    {!accepted && (
                      <button onClick={() => handleResendManager(am.id)} disabled={busy} style={{ fontSize: 12, fontWeight: 700, color: B.steel, background: "none", border: "none", cursor: "pointer", textDecoration: "underline", padding: 0 }}>Resend email</button>
                    )}
                  </div>
                </div>
              );
            })}
          </Card>
        </div>
      </div>

      {/* Edit Account Manager Modal */}
      {mgrOpen && (
        <div onClick={closeMgr} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "16px 16px 100px" }}>
          <div onClick={e => e.stopPropagation()} style={{ background: B.white, borderRadius: 16, width: "100%", maxWidth: 600, maxHeight: "75vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
            <div style={{ background: B.redDark, padding: "18px 24px", borderRadius: "16px 16px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ margin: 0, color: B.white, fontSize: 16, fontWeight: 800 }}>Edit Account Manager</h3>
              <button onClick={closeMgr} style={{ background: "rgba(255,255,255,0.15)", border: "none", color: B.white, borderRadius: 8, width: 30, height: 30, cursor: "pointer", fontSize: 16, fontWeight: 700 }}>✕</button>
            </div>
            <div style={{ padding: "20px 24px" }}>
              <label style={labelStyle}>Name</label>
              <input style={inputStyle} value={mgrForm.name} onChange={e => setMgrField("name", e.target.value)} placeholder="Full name" />

              <label style={labelStyle}>Email</label>
              <input style={inputStyle} type="email" value={mgrForm.email} onChange={e => setMgrField("email", e.target.value)} placeholder="name@example.com" />

              <label style={labelStyle}>Delivery</label>
              <select style={inputStyle} value={mgrForm.delivery} onChange={e => setMgrField("delivery", e.target.value)}>
                <option value="upon_death">Upon death/admin approval</option>
                <option value="immediate">Send immediately</option>
                <option value="scheduled">Schedule a date/time</option>
              </select>

              {mgrForm.delivery === "scheduled" && (
                <>
                  <label style={labelStyle}>Deliver at</label>
                  <input style={inputStyle} type="datetime-local" value={mgrForm.deliverAt ? new Date(mgrForm.deliverAt).toISOString().slice(0,16) : ""} onChange={e => setMgrField("deliverAt", e.target.value)} />
                </>
              )}

              <label style={labelStyle}>Message to recipient</label>
              <textarea style={{ ...inputStyle, height: 100, paddingTop: 8 }} value={mgrForm.message} onChange={e => setMgrField("message", e.target.value)} rows={5} />

              <label style={{ ...labelStyle, display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
                <input type="checkbox" checked={mgrForm.includeSecret} onChange={e => setMgrField("includeSecret", e.target.checked)} />
                Include a private hint for the manager
              </label>

              {mgrForm.includeSecret && (
                <>
                  <label style={labelStyle}>Private hint</label>
                  <textarea style={{ ...inputStyle, height: 80, paddingTop: 8 }} value={mgrForm.secretNote} onChange={e => setMgrField("secretNote", e.target.value)} rows={3} />
                </>
              )}

              {/* Page access */}
              <div style={{ marginTop: 14, padding: 14, border: `1px solid ${B.border}`, borderRadius: 10, background: B.bg }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 8 }}>
                  <div style={{ fontWeight: 800, color: B.redDark }}>Page access</div>
                </div>
                <div style={{ fontSize: 12, color: B.steel, marginBottom: 10 }}>Account Managers are <strong>read-only</strong>. Defaults auto-apply. Grant <strong>extra pages</strong> below.</div>

                <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 8, padding: 10, marginBottom: 10 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 6 }}>Default access (always on)</div>
                  <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                    {DEFAULT_MANAGER_MENU_KEYS.map(k => {
                      const p = PAGE_ACCESS.find(x => x.key === k);
                      return <li key={k}>{p?.label || k}</li>;
                    })}
                  </ul>
                </div>

                <div style={{ background: B.white, border: `1px solid ${B.border}`, borderRadius: 8, padding: 10 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, marginBottom: 8 }}>Optional extra pages</div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {EXTRA_MANAGER_PAGES.map(p => (
                      <label key={p.key} style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 8, alignItems: "start", cursor: "pointer" }}>
                        <input type="checkbox" style={{ marginTop: 2 }} checked={mgrPageAccess[p.key] === true} onChange={e => adminTogglePage(p.key, e.target.checked)} disabled={mgrPermBusy} />
                        <div>
                          <div style={{ fontWeight: 700, fontSize: 13 }}>{p.label}</div>
                          <div style={{ fontSize: 11, color: B.steel, marginTop: 1 }}>{p.description || ""}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div style={{ padding: "0 24px 48px", display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Btn label={mgrEditingId ? "Update" : "Save"} onClick={saveMgr} disabled={busy} color={B.redDark} solid />
              {mgrEditingId && <Btn label={mgrPermBusy ? "Saving..." : "Save permissions"} onClick={savePermissions} disabled={!mgrEditingId || mgrPermBusy} color={B.navy} solid />}
              {mgrEditingId && <Btn label="Delete" onClick={deleteMgr} disabled={busy} color="#dc2626" solid />}
              <Btn label="Cancel" onClick={closeMgr} disabled={busy} color={B.steel} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
